import React, { useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { TrendingUp, TrendingDown, Minus, Users, Heart, MessageCircle, Eye, Target, Lightbulb, BarChart3, RefreshCw, Plus, X, ChevronDown, ChevronUp, Sparkles, Link as LinkIcon, Bookmark, Share2 } from 'lucide-react';
import {
  useGetCompetitorDashboardQuery,
  useMediaAccountsQuery,
  useGetUserEngagementMetricsQuery,
  useRefreshAllCompetitorsMutation,
  useAddCompetitorMutation,
  Platform,
} from 'graphql/generated/graphql';

interface CompetitorDashboardProps {
  campaignId?: string;
}
interface MetricBadgeProps {
  icon: string;
  label: string;
  value: number | null | undefined;
  color?: 'purple' | 'yellow' | 'blue' | 'green';
}

const MetricBadge: React.FC<MetricBadgeProps> = ({ icon, label, value, color = 'purple' }) => {
  const colorClasses = {
    purple: 'bg-purple-500/10 text-purple-400 border-purple-500/20',
    yellow: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20',
    blue: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
    green: 'bg-green-500/10 text-green-400 border-green-500/20'
  };

  if (value === null || value === undefined) return null;

  return (
    <div className={`rounded-lg px-3 py-2 flex items-center gap-2 border ${colorClasses[color]}`}>
      <span className="text-lg">{icon}</span>
      <div>
        <p className="text-xs opacity-70">{label}</p>
        <p className="text-sm font-bold">{value.toLocaleString()}</p>
      </div>
    </div>
  );
};

const CompetitorDashboard: React.FC<CompetitorDashboardProps> = ({ campaignId: propCampaignId }) => {
  const { campaignId: paramCampaignId } = useParams<{ campaignId: string }>();
  const campaignId = propCampaignId ?? paramCampaignId;
  
  const [selectedTab, setSelectedTab] = useState('overview');
  const [showAddCompetitor, setShowAddCompetitor] = useState(false);
  const [expandedInsight, setExpandedInsight] = useState<string | null>(null);
  const [newCompetitorPlatform, setNewCompetitorPlatform] = useState<Platform>(Platform.Instagram);
  const [newCompetitorUsername, setNewCompetitorUsername] = useState('');

  // Fetch real data
  const {
    data: dashboardData,
    loading: dashboardLoading,
    refetch: refetchDashboard
  } = useGetCompetitorDashboardQuery({
    variables: { campaignId: campaignId ?? '' }, // cast fallback
    skip: !campaignId, // ✅ don't fire if undefined
  });
  
  const { data: mediaAccountsData } = useMediaAccountsQuery({});
  const { data: engagementData } = useGetUserEngagementMetricsQuery({});
  const [refreshAllCompetitors, { loading: isRefreshing }] = useRefreshAllCompetitorsMutation();
  const [addCompetitor, { loading: isAddingCompetitor }] = useAddCompetitorMutation();

  // Calculate user's real performance metrics
  const totalFollowers = useMemo(() => {
    return (
      mediaAccountsData?.mediaAccounts?.reduce(
        (sum, acc) => sum + (acc.followerCount ?? 0),
        0
      ) ?? 0
    );
  }, [mediaAccountsData]);

  const engagementRate = useMemo(() => {
    if (!engagementData?.getUserEngagementMetrics || totalFollowers === 0) return 0;
    const totalEngagement =
      engagementData.getUserEngagementMetrics.totalLikes +
      engagementData.getUserEngagementMetrics.totalComments +
      engagementData.getUserEngagementMetrics.totalShares;
    const totalPosts = engagementData.getUserEngagementMetrics.totalPosts;

    if (totalPosts === 0) return 0;

    // Engagement rate = (total engagement / (followers * posts)) * 100
    return (totalEngagement / (totalFollowers * totalPosts)) * 100;
  }, [engagementData, totalFollowers]);

  const postFrequency = useMemo(() => {
    if (!engagementData?.getUserEngagementMetrics) return 0;
    // Posts per day over last 30 days (simplified calculation)
    const totalPosts = engagementData.getUserEngagementMetrics.totalPosts;
    return totalPosts / 30;
  }, [engagementData]);

  // Calculate rank vs competitors
  const rankVsCompetitors = useMemo(() => {
    if (!dashboardData?.getCompetitorDashboard) return 0;
    const competitors = dashboardData.getCompetitorDashboard.competitors || [];
    const allFollowers = [
      totalFollowers,
      ...competitors.map(c => c.latestMetrics?.followers ?? 0)
    ].sort((a, b) => b - a);
    return allFollowers.indexOf(totalFollowers) + 1;
  }, [dashboardData, totalFollowers]);

  const percentile = useMemo(() => {
    if (!dashboardData?.getCompetitorDashboard) return 0;
    const totalAccounts = (dashboardData.getCompetitorDashboard.competitors?.length || 0) + 1;
    return Math.round(((totalAccounts - rankVsCompetitors) / totalAccounts) * 100);
  }, [dashboardData, rankVsCompetitors]);

  const yourPerformance = {
    followers: totalFollowers,
    engagementRate: engagementRate,
    postFrequency: postFrequency,
    rankVsCompetitors: rankVsCompetitors,
    percentile: percentile
  };

  const handleRefreshAll = async () => {
    if (!dashboardData?.getCompetitorDashboard?.config?.id) return;
    try {
      await refreshAllCompetitors({
        variables: { configId: dashboardData.getCompetitorDashboard.config.id }
      });
      await refetchDashboard();
    } catch (error) {
      console.error('Error refreshing competitors:', error);
    }
  };

  const handleAddCompetitor = async () => {
    if (!dashboardData?.getCompetitorDashboard?.config?.id || !newCompetitorUsername) return;
    try {
      await addCompetitor({
        variables: {
          configId: dashboardData.getCompetitorDashboard.config.id,
          platform: newCompetitorPlatform,
          username: newCompetitorUsername
        }
      });
      setShowAddCompetitor(false);
      setNewCompetitorUsername('');
      await refetchDashboard();
    } catch (error) {
      console.error('Error adding competitor:', error);
    }
  };

  const getTrendIcon = (direction: string) => {
    if (direction === 'up') return <TrendingUp className="w-4 h-4 text-green-400" />;
    if (direction === 'down') return <TrendingDown className="w-4 h-4 text-red-400" />;
    return <Minus className="w-4 h-4 text-gray-400" />;
  };

  const getPriorityColor = (priority: string) => {
    if (priority === 'high') return 'border-red-500/50 bg-gradient-to-r from-red-500/10 to-red-600/10';
    if (priority === 'medium') return 'border-yellow-500/50 bg-gradient-to-r from-yellow-500/10 to-yellow-600/10';
    return 'border-blue-500/50 bg-gradient-to-r from-blue-500/10 to-blue-600/10';
  };

  const getInsightIcon = (type: string) => {
    switch (type) {
      case 'opportunity': return <Target className="w-5 h-5 text-green-400" />;
      case 'gap': return <Lightbulb className="w-5 h-5 text-yellow-400" />;
      case 'trend': return <TrendingUp className="w-5 h-5 text-blue-400" />;
      default: return <BarChart3 className="w-5 h-5 text-purple-400" />;
    }
  };

  if (!campaignId) {
    return (
      <div className="min-h-screen p-6 md:p-8 flex items-center justify-center">
        <div className="text-white text-xl">No campaign ID provided</div>
      </div>
    );
  }

  if (dashboardLoading) {
    return (
      <div className="min-h-screen p-6 md:p-8 flex items-center justify-center">
        <div className="text-white text-xl">Loading competitor dashboard...</div>
      </div>
    );
  }

  if (!dashboardData?.getCompetitorDashboard) {
    return (
      <div className="min-h-screen p-6 md:p-8 flex items-center justify-center">
        <div className="text-white text-xl">No competitor data found</div>
      </div>
    );
  }

  const dashboard = dashboardData.getCompetitorDashboard;

  return (
    <div className="min-h-screen p-6 md:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-orange-500 to-yellow-500 flex items-center justify-center shadow-lg shadow-orange-500/30">
                <Target className="w-7 h-7 text-white" />
              </div>
              <div>
                <h1 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-orange-400 to-yellow-400 bg-clip-text text-transparent">
                  Competitor Intelligence
                </h1>
                <p className="text-gray-400 text-sm md:text-base">Track, analyze, and outperform your competition</p>
              </div>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => { handleRefreshAll().catch(console.error); }}
                disabled={isRefreshing}
                className="px-4 py-2.5 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white rounded-xl flex items-center gap-2 transition-all shadow-lg shadow-purple-500/30 hover:shadow-purple-500/50 hover:scale-105 disabled:opacity-50"
              >
                <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
                <span className="hidden sm:inline">{isRefreshing ? 'Refreshing...' : 'Refresh All'}</span>
              </button>
              <button
                onClick={() => setShowAddCompetitor(true)}
                className="px-4 py-2.5 bg-gradient-to-r from-orange-600 to-yellow-600 hover:from-orange-500 hover:to-yellow-500 text-white rounded-xl flex items-center gap-2 transition-all shadow-lg shadow-orange-500/30 hover:shadow-orange-500/50 hover:scale-105"
              >
                <Plus className="w-4 h-4" />
                <span className="hidden sm:inline">Add Competitor</span>
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div className="bg-slate-800/50 backdrop-blur-xl rounded-xl border border-purple-500/20 p-1.5 flex gap-1 overflow-x-auto">
            {['overview', 'competitors', 'insights', 'content'].map((tab) => (
              <button
                key={tab}
                onClick={() => setSelectedTab(tab)}
                className={`px-5 py-2.5 capitalize rounded-lg transition-all flex-shrink-0 font-medium ${
                  selectedTab === tab
                    ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-lg'
                    : 'text-gray-400 hover:text-gray-300 hover:bg-slate-700/50'
                }`}
              >
                {tab}
              </button>
            ))}
          </div>
        </div>

        {/* Your Tracked Accounts */}
        <div className="bg-slate-800/50 backdrop-blur-xl rounded-2xl p-6 md:p-8 border border-purple-500/20 shadow-2xl mb-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-green-500 to-emerald-500 flex items-center justify-center">
              <LinkIcon className="w-5 h-5 text-white" />
            </div>
            <h2 className="text-2xl font-bold text-white">Your Tracked Accounts</h2>
          </div>
          <div className="flex flex-wrap gap-3">
            {dashboard.userAccounts && dashboard.userAccounts.length > 0 ? (
              dashboard.userAccounts.map((account) => (
                <div
                  key={account.id}
                  className="bg-gradient-to-br from-slate-900/50 to-slate-800/50 rounded-xl px-5 py-3 flex items-center gap-3 border border-green-500/20"
                >
                  <div className="w-8 h-8 rounded-full border-2 border-green-500/30 bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white text-xs font-bold">
                    {account.accountName.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <p className="text-white font-semibold text-sm">{account.accountName}</p>
                    <p className="text-gray-400 text-xs">{account.platform}</p>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-gray-400 text-sm">No accounts connected to this campaign</div>
            )}
          </div>
        </div>

        {/* Overview Tab */}
        {selectedTab === 'overview' && (
          <div className="space-y-6">
            {/* Your Performance vs Competitors */}
            <div className="bg-slate-800/50 backdrop-blur-xl rounded-2xl p-6 md:p-8 border border-purple-500/20 shadow-2xl">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                  <Users className="w-5 h-5 text-white" />
                </div>
                <h2 className="text-2xl font-bold text-white">Your Performance</h2>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
                <div className="bg-gradient-to-br from-slate-900/50 to-slate-800/50 rounded-xl p-5 border border-purple-500/10 hover:border-purple-500/30 transition-all hover:shadow-lg hover:shadow-purple-500/10">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-gray-400 text-sm font-medium">Followers</span>
                    <div className="w-10 h-10 rounded-lg bg-purple-500/10 flex items-center justify-center">
                      <Users className="w-5 h-5 text-purple-400" />
                    </div>
                  </div>
                  <div className="text-3xl font-bold text-white mb-2">
                    {yourPerformance.followers.toLocaleString()}
                  </div>
                  <div className="text-sm text-gray-400">
                    Rank #{yourPerformance.rankVsCompetitors} of {(dashboard.competitors?.length || 0) + 1}
                  </div>
                </div>

                <div className="bg-gradient-to-br from-slate-900/50 to-slate-800/50 rounded-xl p-5 border border-red-500/10 hover:border-red-500/30 transition-all hover:shadow-lg hover:shadow-red-500/10">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-gray-400 text-sm font-medium">Engagement Rate</span>
                    <div className="w-10 h-10 rounded-lg bg-red-500/10 flex items-center justify-center">
                      <Heart className="w-5 h-5 text-red-400" />
                    </div>
                  </div>
                  <div className="text-3xl font-bold text-white mb-2">
                    {yourPerformance.engagementRate.toFixed(1)}%
                  </div>
                  <div className={`text-sm flex items-center gap-1 ${
                    yourPerformance.engagementRate < dashboard.aggregateMetrics.avgEngagementRate
                      ? 'text-red-400'
                      : 'text-green-400'
                  }`}>
                    {yourPerformance.engagementRate < dashboard.aggregateMetrics.avgEngagementRate ? (
                      <>
                        <TrendingDown className="w-4 h-4" />
                        Below avg ({dashboard.aggregateMetrics.avgEngagementRate.toFixed(1)}%)
                      </>
                    ) : (
                      <>
                        <TrendingUp className="w-4 h-4" />
                        Above avg ({dashboard.aggregateMetrics.avgEngagementRate.toFixed(1)}%)
                      </>
                    )}
                  </div>
                </div>

                <div className="bg-gradient-to-br from-slate-900/50 to-slate-800/50 rounded-xl p-5 border border-blue-500/10 hover:border-blue-500/30 transition-all hover:shadow-lg hover:shadow-blue-500/10">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-gray-400 text-sm font-medium">Post Frequency</span>
                    <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                      <BarChart3 className="w-5 h-5 text-blue-400" />
                    </div>
                  </div>
                  <div className="text-3xl font-bold text-white mb-2">
                    {yourPerformance.postFrequency.toFixed(1)}/day
                  </div>
                  <div className={`text-sm flex items-center gap-1 ${
                    yourPerformance.postFrequency < dashboard.aggregateMetrics.avgPostFrequency
                      ? 'text-red-400'
                      : 'text-green-400'
                  }`}>
                    {yourPerformance.postFrequency < dashboard.aggregateMetrics.avgPostFrequency ? (
                      <>
                        <TrendingDown className="w-4 h-4" />
                        Below avg ({dashboard.aggregateMetrics.avgPostFrequency.toFixed(1)}/day)
                      </>
                    ) : (
                      <>
                        <TrendingUp className="w-4 h-4" />
                        Above avg ({dashboard.aggregateMetrics.avgPostFrequency.toFixed(1)}/day)
                      </>
                    )}
                  </div>
                </div>

                <div className="bg-gradient-to-br from-slate-900/50 to-slate-800/50 rounded-xl p-5 border border-green-500/10 hover:border-green-500/30 transition-all hover:shadow-lg hover:shadow-green-500/10">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-gray-400 text-sm font-medium">Percentile</span>
                    <div className="w-10 h-10 rounded-lg bg-green-500/10 flex items-center justify-center">
                      <Target className="w-5 h-5 text-green-400" />
                    </div>
                  </div>
                  <div className="text-3xl font-bold text-white mb-2">
                    {yourPerformance.percentile}th
                  </div>
                  <div className={`text-sm ${
                    yourPerformance.percentile < 50 ? 'text-yellow-400' : 'text-green-400'
                  }`}>
                    {yourPerformance.percentile < 50 ? 'Room for improvement' : 'Strong performance'}
                  </div>
                </div>
              </div>
            </div>

            {/* Priority Insights */}
            {dashboard.insights && dashboard.insights.length > 0 && (
              <div className="bg-slate-800/50 backdrop-blur-xl rounded-2xl p-6 md:p-8 border border-purple-500/20 shadow-2xl">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                    <Sparkles className="w-5 h-5 text-white" />
                  </div>
                  <h2 className="text-2xl font-bold text-white">Priority Insights</h2>
                </div>
                <div className="space-y-4">
                  {dashboard.insights.map((insight) => (
                    <div
                      key={insight.id}
                      className={`border-l-4 rounded-xl p-5 ${getPriorityColor(insight.priority)} backdrop-blur-sm transition-all hover:shadow-lg`}
                    >
                      <div className="flex items-start gap-4">
                        <div className="w-10 h-10 flex-shrink-0 rounded-lg bg-slate-900/50 flex items-center justify-center">
                          {getInsightIcon(insight.insightType)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-3 mb-2">
                            <h3 className="font-semibold text-white text-lg">{insight.title}</h3>
                            <button
                              onClick={() => setExpandedInsight(expandedInsight === insight.id ? null : insight.id)}
                              className="flex-shrink-0 text-gray-400 hover:text-white transition-colors p-1 hover:bg-slate-700/50 rounded-lg"
                            >
                              {expandedInsight === insight.id ? (
                                <ChevronUp className="w-5 h-5" />
                              ) : (
                                <ChevronDown className="w-5 h-5" />
                              )}
                            </button>
                          </div>
                          <p className="text-gray-300 text-sm leading-relaxed mb-2">{insight.description}</p>

                          {expandedInsight === insight.id && insight.recommendations && (
                            <div className="mt-4 pt-4 border-t border-slate-700/50">
                              <p className="text-sm text-gray-400 mb-3 font-medium">Recommended Actions:</p>
                              <ul className="space-y-2">
                                {insight.recommendations.map((rec, idx) => (
                                  <li key={idx} className="text-sm text-gray-300 flex items-start gap-3 bg-slate-900/30 rounded-lg p-3">
                                    <span className="text-purple-400 mt-0.5 flex-shrink-0">•</span>
                                    <span>{rec}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Trending Hashtags */}
            {dashboard.aggregateMetrics.hashtagTrends && dashboard.aggregateMetrics.hashtagTrends.length > 0 && (
              <div className="bg-slate-800/50 backdrop-blur-xl rounded-2xl p-6 md:p-8 border border-purple-500/20 shadow-2xl">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-cyan-500 to-blue-500 flex items-center justify-center">
                    <TrendingUp className="w-5 h-5 text-white" />
                  </div>
                  <h2 className="text-2xl font-bold text-white">Trending Hashtags</h2>
                </div>
                <div className="flex flex-wrap gap-3">
                  {dashboard.aggregateMetrics.hashtagTrends.map((tag) => (
                    <div
                      key={tag.hashtag}
                      className="bg-gradient-to-br from-slate-900/50 to-slate-800/50 rounded-xl px-5 py-3 flex items-center gap-3 border border-purple-500/10 hover:border-purple-500/30 transition-all hover:shadow-lg hover:shadow-purple-500/10"
                    >
                      <span className="text-purple-400 font-semibold">{tag.hashtag}</span>
                      <span className="text-gray-600">•</span>
                      <span className="text-gray-400 text-sm">{tag.count} uses</span>
                      <div className="w-8 h-8 rounded-lg bg-green-500/10 flex items-center justify-center">
                        <TrendingUp className="w-4 h-4 text-green-400" />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Competitors Tab */}
        {selectedTab === 'competitors' && (
          <div className="space-y-4">
            {dashboard.competitors && dashboard.competitors.length > 0 ? (
              dashboard.competitors.map((competitor) => (
                <div
                  key={competitor.id}
                  className="bg-slate-800/50 backdrop-blur-xl rounded-2xl p-6 md:p-8 border border-purple-500/20 shadow-2xl hover:shadow-purple-500/10 transition-all"
                >
                  <div className="flex flex-col md:flex-row items-start gap-6">
                    <div className="relative">
                      <div className="w-20 h-20 rounded-2xl border-2 border-purple-500 shadow-lg bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white text-2xl font-bold">
                        {competitor.username.charAt(0).toUpperCase()}
                      </div>
                      <div className="absolute -bottom-2 -right-2 w-8 h-8 rounded-lg bg-gradient-to-br from-orange-500 to-yellow-500 flex items-center justify-center shadow-lg">
                        <Target className="w-4 h-4 text-white" />
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
                        <div>
                          <h3 className="text-xl font-bold text-white mb-1">{competitor.displayName ?? competitor.username}</h3>
                          <p className="text-gray-400">{competitor.username} • {competitor.platform}</p>
                        </div>
                        {competitor.growthTrend && (
                          <div className="flex items-center gap-2 bg-slate-900/50 rounded-lg px-4 py-2 border border-slate-700">
                            {getTrendIcon(competitor.growthTrend.direction)}
                            <span className={`font-bold ${competitor.growthTrend.direction === 'up' ? 'text-green-400' : competitor.growthTrend.direction === 'down' ? 'text-red-400' : 'text-gray-400'}`}>
                              {competitor.growthTrend.percentage.toFixed(1)}%
                            </span>
                            <span className="text-gray-500 text-sm">{competitor.growthTrend.timeframe}</span>
                          </div>
                        )}
                      </div>

                      {competitor.latestMetrics && (
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                          <div className="bg-slate-900/30 rounded-xl p-4 border border-slate-700/50">
                            <p className="text-gray-400 text-xs mb-2 flex items-center gap-2">
                              <Users className="w-3.5 h-3.5" />
                              Followers
                            </p>
                            <p className="text-white font-bold text-lg">{competitor.latestMetrics.followers.toLocaleString()}</p>
                          </div>
                          <div className="bg-slate-900/30 rounded-xl p-4 border border-slate-700/50">
                            <p className="text-gray-400 text-xs mb-2 flex items-center gap-2">
                              <Heart className="w-3.5 h-3.5" />
                              Engagement
                            </p>
                            <p className="text-white font-bold text-lg">{competitor.latestMetrics.engagementRate.toFixed(1)}%</p>
                          </div>
                          <div className="bg-slate-900/30 rounded-xl p-4 border border-slate-700/50">
                            <p className="text-gray-400 text-xs mb-2 flex items-center gap-2">
                              <BarChart3 className="w-3.5 h-3.5" />
                              Posts/Day
                            </p>
                            <p className="text-white font-bold text-lg">{competitor.latestMetrics.postFrequency.toFixed(1)}</p>
                          </div>
                          <div className="bg-slate-900/30 rounded-xl p-4 border border-slate-700/50">
                            <p className="text-gray-400 text-xs mb-2 flex items-center gap-2">
                              <TrendingUp className="w-3.5 h-3.5" />
                              Growth
                            </p>
                            <p className="text-white font-bold text-lg">{competitor.latestMetrics.followersGrowth.toLocaleString()}</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="bg-slate-800/50 backdrop-blur-xl rounded-2xl p-12 border border-purple-500/20 text-center">
                <Users className="w-16 h-16 text-purple-400 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-white mb-2">No Competitors Added Yet</h3>
                <p className="text-gray-400 mb-6">Add competitors to start tracking their performance</p>
                <button
                  onClick={() => setShowAddCompetitor(true)}
                  className="px-6 py-3 bg-gradient-to-r from-orange-600 to-yellow-600 hover:from-orange-500 hover:to-yellow-500 text-white rounded-xl transition-all shadow-lg"
                >
                  Add Your First Competitor
                </button>
              </div>
            )}
          </div>
        )}

        {/* Insights Tab */}
        {selectedTab === 'insights' && (
          <div className="bg-slate-800/50 backdrop-blur-xl rounded-2xl p-6 md:p-8 border border-purple-500/20 shadow-2xl">
            {dashboard.insights && dashboard.insights.length > 0 ? (
              <div className="space-y-4">
                {dashboard.insights.map((insight) => (
                  <div
                    key={insight.id}
                    className={`border-l-4 rounded-xl p-5 ${getPriorityColor(insight.priority)} backdrop-blur-sm transition-all hover:shadow-lg`}
                  >
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 flex-shrink-0 rounded-lg bg-slate-900/50 flex items-center justify-center">
                        {getInsightIcon(insight.insightType)}
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold text-white text-lg mb-2">{insight.title}</h3>
                        <p className="text-gray-300 text-sm leading-relaxed">{insight.description}</p>
                        {insight.recommendations && insight.recommendations.length > 0 && (
                          <div className="mt-4 pt-4 border-t border-slate-700/50">
                            <p className="text-sm text-gray-400 mb-3 font-medium">Recommended Actions:</p>
                            <ul className="space-y-2">
                              {insight.recommendations.map((rec, idx) => (
                                <li key={idx} className="text-sm text-gray-300 flex items-start gap-3 bg-slate-900/30 rounded-lg p-3">
                                  <span className="text-purple-400 mt-0.5">•</span>
                                  <span>{rec}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <Lightbulb className="w-16 h-16 text-yellow-400 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-white mb-2">No Insights Yet</h3>
                <p className="text-gray-400">Insights will appear as we analyze competitor behavior</p>
              </div>
            )}
          </div>
        )}

        {/* Content Tab */}
        {selectedTab === 'content' && (
          <div className="space-y-6">
            {/* Top Saved Content */}
            {dashboard.topSavedContent && dashboard.topSavedContent.length > 0 && (
              <div>
                <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                  <span className="text-2xl">🔖</span>
                  Most Saved Content
                </h3>
                <div className="space-y-4">
                  {dashboard.topSavedContent.map((post) => (
                    <div key={post.id} className="bg-slate-800/50 backdrop-blur-xl rounded-2xl p-6 border border-purple-500/20">
                      <div className="flex gap-6">
                        {post.thumbnailUrl && (
                          <img src={post.thumbnailUrl} className="w-32 h-32 rounded-xl object-cover" alt="" />
                        )}
                        
                        <div className="flex-1">
                          <p className="text-gray-300 text-sm mb-4 line-clamp-2">{post.caption}</p>
                          
                          <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
                            <MetricBadge icon="❤️" label="Likes" value={post.likes} />
                            <MetricBadge icon="💬" label="Comments" value={post.comments} />
                            <MetricBadge icon="📤" label="Shares" value={post.shares} />
                            <MetricBadge icon="🔖" label="Saves" value={post.saves} color="yellow" />
                            {post.fbLikeCount && (
                              <MetricBadge icon="👍" label="FB Likes" value={post.fbLikeCount} color="blue" />
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Top Shared Content */}
            {dashboard.topSharedContent && dashboard.topSharedContent.length > 0 && (
              <div>
                <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                  <span className="text-2xl">📤</span>
                  Most Shared Content
                </h3>
                <div className="space-y-4">
                  {dashboard.topSharedContent.map((post) => (
                    <div key={post.id} className="bg-slate-800/50 backdrop-blur-xl rounded-2xl p-6 border border-purple-500/20">
                      <div className="flex gap-6">
                        {post.thumbnailUrl && (
                          <img src={post.thumbnailUrl} className="w-32 h-32 rounded-xl object-cover" alt="" />
                        )}
                        
                        <div className="flex-1">
                          <p className="text-gray-300 text-sm mb-4 line-clamp-2">{post.caption}</p>
                          
                          <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
                            <MetricBadge icon="❤️" label="Likes" value={post.likes} />
                            <MetricBadge icon="💬" label="Comments" value={post.comments} />
                            <MetricBadge icon="📤" label="Shares" value={post.shares} color="green" />
                            <MetricBadge icon="🔖" label="Saves" value={post.saves} color="yellow" />
                            {post.fbLikeCount && (
                              <MetricBadge icon="👍" label="FB Likes" value={post.fbLikeCount} color="blue" />
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* No content state */}
            {(!dashboard.topSavedContent || dashboard.topSavedContent.length === 0) && 
            (!dashboard.topSharedContent || dashboard.topSharedContent.length === 0) && (
              <div className="bg-slate-800/50 backdrop-blur-xl rounded-2xl p-6 md:p-8 border border-purple-500/20 shadow-2xl">
                <div className="text-center py-12">
                  <MessageCircle className="w-16 h-16 text-purple-400 mx-auto mb-4" />
                  <h3 className="text-xl font-bold text-white mb-2">No Content Yet</h3>
                  <p className="text-gray-400">Content will appear after competitors are added and scraped</p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Add Competitor Modal */}
        {showAddCompetitor && (
          <div className="fixed inset-0 bg-black/80 backdrop-blur-md flex items-center justify-center z-50 p-4">
            <div className="bg-slate-800/95 backdrop-blur-xl rounded-2xl p-6 md:p-8 max-w-md w-full border border-purple-500/20 shadow-2xl shadow-purple-500/20">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-orange-500 to-yellow-500 flex items-center justify-center">
                    <Plus className="w-5 h-5 text-white" />
                  </div>
                  <h2 className="text-2xl font-bold text-white">Add Competitor</h2>
                </div>
                <button
                  onClick={() => setShowAddCompetitor(false)}
                  className="text-gray-400 hover:text-white transition-colors p-2 hover:bg-slate-700 rounded-lg"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-5">
                <div>
                  <label className="block text-gray-300 text-sm font-medium mb-2">Platform</label>
                  <select
                    value={newCompetitorPlatform}
                    onChange={(e) => setNewCompetitorPlatform(e.target.value as Platform)}
                    className="w-full bg-slate-900/50 border border-purple-500/20 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 transition-all"
                  >
                    <option value={Platform.Instagram}>Instagram</option>
                    <option value={Platform.TikTok}>TikTok</option>
                    <option value={Platform.Youtube}>YouTube</option>
                  </select>
                </div>

                <div>
                  <label className="block text-gray-300 text-sm font-medium mb-2">Username</label>
                  <input
                    type="text"
                    placeholder="@competitor"
                    value={newCompetitorUsername}
                    onChange={(e) => setNewCompetitorUsername(e.target.value)}
                    className="w-full bg-slate-900/50 border border-purple-500/20 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 transition-all"
                  />
                </div>

                <button
                  onClick={() => { handleAddCompetitor().catch(console.error); }}
                  disabled={isAddingCompetitor || !newCompetitorUsername}
                  className="w-full bg-gradient-to-r from-orange-600 to-yellow-600 hover:from-orange-500 hover:to-yellow-500 text-white rounded-xl px-6 py-3.5 transition-all font-medium shadow-lg shadow-orange-500/30 hover:shadow-orange-500/50 hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isAddingCompetitor ? 'Adding...' : 'Add Competitor'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CompetitorDashboard;